'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { usePrivateMode } from '@/lib/privateMode';
import { useConfirm } from './ConfirmDialog';

export type LlmBackend = 'qwen' | 'dolphin' | 'qwen_uncensored';
export type LlmChoice = 'auto' | LlmBackend;

const BACKEND_LABELS: Record<LlmBackend, string> = {
  qwen: 'Qwen',
  dolphin: 'Dolphin',
  qwen_uncensored: 'Qwen Uncensored',
};

function isBackend(value: string | null): value is LlmBackend {
  return value === 'qwen' || value === 'dolphin' || value === 'qwen_uncensored';
}

/**
 * LLM backend selection shared by the builder agent and the variants page.
 *
 * 'auto' resolves to `privateAuto` in private mode and qwen otherwise, mirroring whichever
 * backend the caller talks to. The builder agent defaults to dolphin; the collage assistant
 * passes 'qwen', because there an uncensored model must be an explicit choice rather than
 * something a user lands on without asking.
 *
 * Explicit picks persist per storageKey, so each feature keeps its own preference.
 */
export function useLlmChoice(storageKey: string, privateAuto: LlmBackend = 'dolphin') {
  const privateEnabled = usePrivateMode(state => state.enabled);
  const { confirm } = useConfirm();

  const [choice, setChoice] = useState<LlmChoice>('auto');
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (isBackend(stored)) setChoice(stored);
    } catch { /* stay on auto */ }
  }, [storageKey]);

  const resolved: LlmBackend = choice === 'auto' ? (privateEnabled ? privateAuto : 'qwen') : choice;

  // Low-VRAM guard: loading an LM Studio model into a nearly-full GPU can fail or
  // evict other models — confirm once per model per session when it looks tight.
  const gpuConfirmedRef = useRef<Set<string>>(new Set());
  const confirmGpu = useCallback(async (llm: LlmBackend): Promise<boolean> => {
    if (llm === 'qwen' || gpuConfirmedRef.current.has(llm)) return true;
    try {
      const status = await api.getLlmStatus();
      const backend = status.backends.find(b => b.name === llm);
      if (!backend || backend.loaded !== false || !status.gpu) return true;
      const needed = backend.est_need_gb + 2; // context/CUDA overhead margin
      if (status.gpu.free_gb >= needed) return true;
      const ok = await confirm({
        title: 'GPU memory warning',
        message: `Loading ${backend.model} needs ~${backend.est_need_gb} GB but only ` +
          `${status.gpu.free_gb} GB of GPU memory is free (${status.gpu.used_gb}/${status.gpu.total_gb} GB used). ` +
          `Loading may fail or evict other models (image generation, the other LLM). Continue?`,
        confirmText: 'Load anyway',
      });
      if (ok) gpuConfirmedRef.current.add(llm);
      return ok;
    } catch {
      return true; // status unavailable — do not block
    }
  }, [confirm]);

  const change = useCallback(async (value: LlmChoice) => {
    const target: LlmBackend = value === 'auto' ? (privateEnabled ? privateAuto : 'qwen') : value;
    if (!(await confirmGpu(target))) return; // keep the previous selection
    setChoice(value);
    try {
      if (value === 'auto') localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, value);
    } catch { /* non-persistent is fine */ }
  }, [confirmGpu, privateEnabled, privateAuto, storageKey]);

  return { choice, resolved, change, confirmGpu };
}

interface LlmPickerProps {
  choice: LlmChoice;
  resolved: LlmBackend;
  onChange: (value: LlmChoice) => void;
  disabled?: boolean;
  className?: string;
  title?: string;
}

/** The backend <select>. Pair with useLlmChoice, which owns persistence and the VRAM guard. */
export default function LlmPicker({ choice, resolved, onChange, disabled, className, title }: LlmPickerProps) {
  return (
    <select
      value={choice}
      disabled={disabled}
      onChange={e => onChange(e.target.value as LlmChoice)}
      className={className ?? 'text-xs bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg px-2 py-1 text-[var(--text-secondary)] cursor-pointer'}
      title={title ?? 'LLM backend — Auto uses the uncensored Dolphin in private mode, Qwen otherwise'}
    >
      <option value="auto">Auto ({BACKEND_LABELS[resolved]})</option>
      <option value="qwen">Qwen</option>
      <option value="dolphin">Dolphin</option>
      <option value="qwen_uncensored">Qwen Uncensored</option>
    </select>
  );
}
